import { useRef, useState } from "react"
import { ChatPanel } from "./ChatPanel"

const MARGIN = 16
const PANEL_WIDTH = 400
const MIN_WIDTH = 320
const MIN_HEIGHT = 360

interface DragOrigin {
  pointerX: number
  pointerY: number
  panelX: number
  panelY: number
}

interface ResizeOrigin {
  pointerX: number
  pointerY: number
  right: number
  bottom: number
  width: number
  height: number
}

export function FloatingChatPanel() {
  const cardRef = useRef<HTMLDivElement>(null)
  const dragOriginRef = useRef<DragOrigin | null>(null)
  const resizeOriginRef = useRef<ResizeOrigin | null>(null)
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  )

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // React bubbles events from portals through the component tree, so a
    // pointerdown inside the header's portaled dropdown menu reaches this
    // handler too. Starting a drag then captures the pointer, which redirects
    // the pointerup away from the menu item and swallows its selection. Only
    // start a drag when the pointer physically lands on the header itself.
    if (
      !(event.target instanceof Node) ||
      !event.currentTarget.contains(event.target)
    ) {
      return
    }
    // Ignore drags that start on the header's buttons.
    if ((event.target as HTMLElement).closest("button")) {
      return
    }
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }
    dragOriginRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      panelX: rect.left,
      panelY: rect.top,
    }
    // Optional chaining: jsdom (tests) doesn't implement pointer capture.
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.buttons !== 1) {
      return
    }
    const origin = dragOriginRef.current
    const rect = cardRef.current?.getBoundingClientRect()
    if (!origin || !rect) {
      return
    }
    const x = origin.panelX + event.clientX - origin.pointerX
    const y = origin.panelY + event.clientY - origin.pointerY
    setPosition({
      x: Math.min(Math.max(x, 0), window.innerWidth - rect.width),
      y: Math.min(Math.max(y, 0), window.innerHeight - rect.height),
    })
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOriginRef.current) {
      return
    }
    dragOriginRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  const handleResizePointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }
    resizeOriginRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const handleResizePointerMove = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (event.buttons !== 1) {
      return
    }
    const origin = resizeOriginRef.current
    if (!origin) {
      return
    }
    // The bottom-right corner stays fixed; dragging the top-left corner
    // up/left grows the panel. Cap at the origin's right/bottom edge so the
    // top-left corner cannot leave the viewport.
    const width = Math.min(
      Math.max(origin.width + origin.pointerX - event.clientX, MIN_WIDTH),
      origin.right,
    )
    const height = Math.min(
      Math.max(origin.height + origin.pointerY - event.clientY, MIN_HEIGHT),
      origin.bottom,
    )
    setSize({ width, height })
    if (position) {
      setPosition({ x: origin.right - width, y: origin.bottom - height })
    }
  }

  const handleResizePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeOriginRef.current) {
      return
    }
    resizeOriginRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  return (
    <div
      ref={cardRef}
      data-testid="floating-chat-panel"
      className="fixed z-40 flex h-[min(620px,80vh)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
      style={{
        width: size?.width ?? PANEL_WIDTH,
        // Falls back to the h-[min(620px,80vh)] class until the user resizes.
        height: size?.height,
        maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
        maxHeight: `calc(100vh - ${MARGIN * 2}px)`,
        ...(position
          ? { left: position.x, top: position.y }
          : { right: MARGIN, bottom: MARGIN }),
      }}
    >
      <div
        data-testid="floating-chat-resize-handle"
        aria-hidden="true"
        className="absolute left-0 top-0 z-10 h-4 w-4 cursor-nwse-resize"
        style={{ touchAction: "none" }}
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onPointerUp={handleResizePointerUp}
        onPointerCancel={handleResizePointerUp}
      />
      <ChatPanel
        className="rounded-xl"
        dragHandleProps={{
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerUp,
          onPointerCancel: handlePointerUp,
          style: { cursor: "grab", touchAction: "none", userSelect: "none" },
        }}
      />
    </div>
  )
}
